export type ThemeType = 'dark' | 'light';
export type ThemePreference = ThemeType | 'system';

export const THEME_STORAGE_KEY = 'theme';
export const THEME_PREFERENCE_CHANGE_EVENT = 'wavvest-theme-preference-change';

const PREFERS_LIGHT_QUERY = '(prefers-color-scheme: light)';

export const readSavedThemePreference = (): ThemePreference | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system'
      ? savedTheme
      : null;
  } catch {
    return null;
  }
};

export const readSystemTheme = (): ThemeType => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }

  return window.matchMedia(PREFERS_LIGHT_QUERY).matches ? 'light' : 'dark';
};

export const resolveThemePreference = (
  preference: ThemePreference,
  systemTheme = readSystemTheme(),
): ThemeType => (preference === 'system' ? systemTheme : preference);

export const writeSavedThemePreference = (preference: ThemePreference): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Keep the caller's in-memory choice; subscribers would re-read storage
    // and revert it to the previous persisted value.
    return;
  }

  window.dispatchEvent(new Event(THEME_PREFERENCE_CHANGE_EVENT));
};

export const subscribeToSavedThemePreference = (onChange: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === THEME_STORAGE_KEY) {
      onChange();
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(THEME_PREFERENCE_CHANGE_EVENT, onChange);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(THEME_PREFERENCE_CHANGE_EVENT, onChange);
  };
};

export const subscribeToSystemTheme = (onChange: () => void): (() => void) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }

  const query = window.matchMedia(PREFERS_LIGHT_QUERY);
  query.addEventListener?.('change', onChange);

  return () => query.removeEventListener?.('change', onChange);
};
